import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const WorkspaceContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState({ total: 0, in_progress: 0, completed_today: 0, inbox: 0 });
  const [loading, setLoading] = useState(true);  // Start with true

  const fetchWorkspaces = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/workspaces`, { withCredentials: true });
      setWorkspaces(data);
      return data;
    } catch (e) {
      console.error('Error fetching workspaces:', e);
      return [];
    }
  }, [user]);

  const fetchTasks = useCallback(async (workspaceId) => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tasks/${workspaceId}`, { withCredentials: true });
      setTasks(data);
    } catch (e) {
      console.error('Error fetching tasks:', e);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTags = useCallback(async (workspaceId) => {
    if (!workspaceId) return;
    try {
      const { data } = await axios.get(`${API}/tags/${workspaceId}`, { withCredentials: true });
      setTags(data);
    } catch (e) {
      console.error('Error fetching tags:', e);
      setTags([]);
    }
  }, []);

  const fetchStats = useCallback(async (workspaceId) => {
    if (!workspaceId) return;
    try {
      const { data } = await axios.get(`${API}/stats/${workspaceId}`, { withCredentials: true });
      setStats(data);
    } catch (e) {
      console.error('Error fetching stats:', e);
    }
  }, []);

  const createWorkspace = async (name, type = 'personal', sharedWith = [], icon = 'bolt') => {
    try {
      const { data } = await axios.post(`${API}/workspaces`, {
        name,
        type,
        shared_with: sharedWith,
        icon
      }, { withCredentials: true });
      setWorkspaces(prev => [...prev, data]);
      return data;
    } catch (e) {
      console.error('Error creating workspace:', e);
      throw e;
    }
  };

  const updateWorkspace = async (workspaceId, updates) => {
    try {
      const { data } = await axios.patch(`${API}/workspaces/${workspaceId}`, updates, { withCredentials: true });
      setWorkspaces(prev => prev.map(w => w.id === workspaceId ? { ...w, ...data } : w));
      if (currentWorkspace?.id === workspaceId) {
        setCurrentWorkspace(prev => ({ ...prev, ...data }));
      }
      return data;
    } catch (e) {
      console.error('Error updating workspace:', e);
      throw e;
    }
  };

  const inviteToWorkspace = async (workspaceId, email) => {
    try {
      const { data } = await axios.post(`${API}/workspaces/${workspaceId}/invite`, { email }, { withCredentials: true });
      return data;
    } catch (e) {
      console.error('Error inviting to workspace:', e);
      throw e;
    }
  };

  const createTask = async (title, description = '', tag = '', priority = 'medium') => {
    if (!currentWorkspace) return;
    try {
      const { data } = await axios.post(`${API}/tasks`, {
        title,
        description,
        tag,
        priority,
        workspace_id: currentWorkspace.id
      }, { withCredentials: true });
      setTasks(prev => [...prev, data]);
      await fetchStats(currentWorkspace.id);
      return data;
    } catch (e) {
      console.error('Error creating task:', e);
      throw e;
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      const { data } = await axios.patch(`${API}/tasks/${taskId}`, updates, { withCredentials: true });
      setTasks(prev => prev.map(t => t.id === taskId ? data : t));
      if (currentWorkspace) await fetchStats(currentWorkspace.id);
      return data;
    } catch (e) {
      console.error('Error updating task:', e);
      throw e;
    }
  };

  const moveTask = async (taskId, status, order) => {
    try {
      await axios.patch(`${API}/tasks/${taskId}/move`, { status, order }, { withCredentials: true });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status, order } : t));
      if (currentWorkspace) await fetchStats(currentWorkspace.id);
    } catch (e) {
      console.error('Error moving task:', e);
      throw e;
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
      setTasks(prev => prev.filter(t => t.id !== taskId));
      if (currentWorkspace) await fetchStats(currentWorkspace.id);
    } catch (e) {
      console.error('Error deleting task:', e);
      throw e;
    }
  };

  useEffect(() => {
    const initWorkspaces = async () => {
      if (user) {
        const data = await fetchWorkspaces();
        if (data && data.length > 0) {
          setCurrentWorkspace(data[0]);
        } else {
          setLoading(false);
        }
      }
    };
    initWorkspaces();
  }, [user, fetchWorkspaces]);

  useEffect(() => {
    if (currentWorkspace) {
      fetchTasks(currentWorkspace.id);
      fetchTags(currentWorkspace.id);
      fetchStats(currentWorkspace.id);
    }
  }, [currentWorkspace, fetchTasks, fetchTags, fetchStats]);

  const switchWorkspace = (workspace) => {
    setCurrentWorkspace(workspace);
  };

  return (
    <WorkspaceContext.Provider value={{
      workspaces,
      currentWorkspace,
      tasks,
      tags,
      setTags,
      stats,
      loading,
      fetchWorkspaces,
      fetchTasks,
      fetchTags,
      fetchStats,
      createWorkspace,
      updateWorkspace,
      inviteToWorkspace,
      createTask,
      updateTask,
      moveTask,
      deleteTask,
      switchWorkspace
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
